/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import Head from "next/head";
import Link from "next/link";

import {
  LDBlockDate,
  LDBlockHeading,
  LDBlockParagraph,
  LDBlockQuote,
  LDBlockSubheading,
} from "src/components/Splash/Layout/Typography";
import { LDBlock } from "src/components/Splash/LDBlock";
import { PostAttrs, PostTemplate } from "src/components/Splash/PostTemplate";
import { ExampleGallery, ViewOnlyPlateThumbnail } from "src/components/Splash/ViewOnlyPlateThumbnail";
import { AppRoutes } from "src/lib/constants";

export const FUNDING_POST_ATTRS: PostAttrs = {
  title: "VIENNA RAISES CAPITAL",
  SEOtitle: "Vienna Raises Capital",
  date: "September 16, 2022",
  slug: "funding-2022",
  description:
    "We are pleased to announce that we have raised $2.25m, with participation from existing investors 1517 Fund and Peter Thiel...",
};

const EXAMPLE_PLATES = [
  {
    title: "engineered-eternity",
    url: "https://vienna.earth/plate/saneel/engineered-eternity",
    thumbnailUrl: "https://d1qx4aitdy9duy.cloudfront.net/cccd597a-cdf8-4846-8091-1aa618671c25-light.png",
  },
  {
    title: "Gustav Klimt",
    url: "https://vienna.earth/plate/artvee/Gustav%20Klimt",
    thumbnailUrl: "https://d1qx4aitdy9duy.cloudfront.net/91f7e570-71fc-456d-b3ec-0337cabba76c-light.png",
  },
  {
    title: "reminiscing-our-tomorrow",
    url: "https://vienna.earth/plate/gdbroman/reminiscing-our-tomorrow",
    thumbnailUrl: "https://d1qx4aitdy9duy.cloudfront.net/26aada17-6c4b-4316-ba14-8bd18dc12e87-light.png",
  },
  {
    title: "The Tsar and His Railway",
    url: "https://vienna.earth/plate/nine/The%20Tsar%20and%20the%20Railway",
    thumbnailUrl: "https://d1qx4aitdy9duy.cloudfront.net/29b71f76-ad7d-46a5-8514-c3c7ab339578-light.png",
  },
];

export default function FundingPost() {
  return (
    <PostTemplate leftLink={AppRoutes.Meta} leftLinkText="HOME" rightLink="" rightLinkText="">
      <Head>
        <title>{FUNDING_POST_ATTRS.SEOtitle} - Blog - Vienna Hypertext</title>
        <meta name="description" content={FUNDING_POST_ATTRS.description} />

        {/* twitter card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@vienna_earth" />
        <meta name="twitter:title" content={FUNDING_POST_ATTRS.SEOtitle} />
        <meta name="twitter:description" content={FUNDING_POST_ATTRS.description} />
      </Head>
      <LDBlock>
        <LDBlockHeading>{FUNDING_POST_ATTRS.title}</LDBlockHeading>
        <LDBlockDate>{FUNDING_POST_ATTRS.date}</LDBlockDate>
        <LDBlockParagraph>
          We are pleased to announce that we have raised $2.25m, with participation from existing investors 1517
          Fund and Peter Thiel, and new investors FJ Labs, Assembly Capital, Polymath Capital, FAST by GETTYLAB,
          Dave Petersen, and many others.
        </LDBlockParagraph>
        <LDBlockParagraph>
          We will use this opportunity to expand our team and advance toward our vision of{" "}
          <Link href="https://vienna.earth/plate/vht/Computer%20Renaissance">digital renaissance</Link>. We are
          actively hiring. If you have skills and want to grind in SoHo with us, apply{" "}
          <Link href={AppRoutes.Jobs}>here</Link>.
        </LDBlockParagraph>
        <LDBlockSubheading>The Kritik</LDBlockSubheading>
        <LDBlockParagraph>
          In the early days of personal computing, people imagined a future where the computer would be the
          ultimate tool for human flourishing. Douglas Engelbart, Ted Nelson, Steve Jobs, and other pioneers shared
          the conviction that great software could unlock some latent potential in man.
        </LDBlockParagraph>
        <LDBlockParagraph>
          That is not the world we live in today. If anything our computers and phones have become portals into a
          homogenous and{" "}
          <Link href="https://twitter.com/viennahypertext/status/1511421118489452544">de-energized dystopia</Link>.
          Despite theoretical access to the most information in history, we are more and more reading the same
          things, lodging the same complaints, and sharing the same memes.
        </LDBlockParagraph>
        <LDBlockParagraph>
          Instead of great tools we got dumb media; instead of digital renaissance we got mediocre cyberpunk.
        </LDBlockParagraph>
        <LDBlockSubheading>The Plan</LDBlockSubheading>
        <LDBlockParagraph>
          Vienna is building a new kind of information network. It will be the digital space where great ideas and
          projects begin. By combining the ethos of early personal computing with the power of modern communication
          networks, Vienna will make good on many of the dreams of the ‘60s and ‘70s that fell into obscurity
          during the gold-rush phase of web2.
        </LDBlockParagraph>
        <LDBlockParagraph>Here are some examples of what’s being built on the network:</LDBlockParagraph>
        <ExampleGallery>
          {EXAMPLE_PLATES.map((plate) => (
            <ViewOnlyPlateThumbnail
              title={plate.title}
              url={plate.url}
              thumbnailUrl={plate.thumbnailUrl}
              key={plate.title}
            />
          ))}
        </ExampleGallery>
        <LDBlockParagraph>
          Early Vienna enjoyers have been prolific creators and many have made Vienna their digital home. Both
          qualitative feedback and measured engagement have been extremely strong. A closed-beta user wrote:
        </LDBlockParagraph>
        <LDBlockQuote>
          &gt; Vienna is fun and pleasing as simple idea expression. I’ve had to set a time limit for my use in my
          OS settings. I simply love to be here and enjoy the design as a user. A refuge from the rest of the
          internet.
        </LDBlockQuote>
        <LDBlockParagraph>
          In the near future, we look forward to expanding access to the network.
        </LDBlockParagraph>
        <LDBlockParagraph>
          Expect to hear much more from us in the future, including at{" "}
          <Link href="https://assembly.urbit.org/">Urbit Assembly</Link>, where we will have a big announcement,
          later this month.
        </LDBlockParagraph>
      </LDBlock>
    </PostTemplate>
  );
}
